// Brand Colors
export const BRAND = {
  PRIMARY: '#1D3DFC',    // Primary brand color
  SECONDARY: '#3E97FF',  // Secondary brand color
  SUCCESS: '#00CC85',    // Success actions/states
  WARNING: '#F1BC00',    // Warning actions/states
  DANGER: '#dd435b',     // Danger/Error actions/states
  INFO: '#3E97FF',       // Information actions/states
};

// Tier Colors for Framework Tiers
export const TIER_COLORS = {
  TIER1: '#607d8b', // Grey like classical color
  TIER2: '#B08D57', // Bronze
  TIER3: '#C0C0C0', // Silver
  TIER4: '#FFDF00', // Gold
  TIER5: '#212121', // Black
};

// Risk Level Colors
export const RISK_LEVEL = {
  CRITICAL: {
    MAIN: '#dd435b',      // Critical Risk - Level 5
    LIGHT: '#FFF5F8',     // Critical Risk background
    BORDER: '#dd435b40',  // Critical Risk border
  },
  HIGH: {
    MAIN: '#ee9551',      // High Risk - Level 4
    LIGHT: '#FFF8F3',     // High Risk background
    BORDER: '#ee955140',  // High Risk border
  },
  MODERATE: {
    MAIN: '#F1BC00',      // Moderate Risk - Level 3
    LIGHT: '#FFF8DD',     // Moderate Risk background
    BORDER: '#F1BC0040',  // Moderate Risk border
  },
  LOW: {
    MAIN: '#70B944',      // Low Risk - Level 2
    LIGHT: '#E8FFF3',     // Low Risk background
    BORDER: '#70B94440',  // Low Risk border
  },
  VERY_LOW: {
    MAIN: '#498e30',      // Very Low Risk - Level 1
    LIGHT: '#E8FFF3',     // Very Low Risk background
    BORDER: '#498e3040',  // Very Low Risk border
  }
};

// Risk Type Colors
export const RISK_TYPE = {
  INFRASTRUCTURE: {
    MAIN: BRAND.PRIMARY,
    LIGHT: '#F1F3FF',
    BORDER: `${BRAND.PRIMARY}40`
  },
  DATA_BREACH: {
    MAIN: '#7239EA',
    LIGHT: '#F8F5FF',
    BORDER: '#7239EA40'
  },
  SUPPLY_CHAIN: {
    MAIN: BRAND.SUCCESS,
    LIGHT: '#E8FFF3',
    BORDER: `${BRAND.SUCCESS}40`
  },
  REGULATORY: {
    MAIN: BRAND.WARNING,
    LIGHT: '#FFF8DD',
    BORDER: `${BRAND.WARNING}40`
  },
  HUMAN_FACTOR: {
    MAIN: BRAND.DANGER,
    LIGHT: '#FFF5F8',
    BORDER: `${BRAND.DANGER}40`
  }
};

// Text Colors
export const TEXT = {
  PRIMARY: '#181C32',     // Primary text
  SECONDARY: '#5E6278',   // Secondary text
  MUTED: '#A1A5B7',      // Muted text
  INVERSE: '#FFFFFF',     // Text on dark backgrounds
};

// Background Colors
export const BACKGROUND = {
  MAIN: '#FFFFFF',        // Main background
  SECONDARY: '#F9F9F9',   // Secondary background
  LIGHT: '#F5F8FA',      // Light background
  HOVER: '#F5F8FE',      // Hover state background
  SELECTED: '#EEF6FF',   // Selected state background
};

// Border Colors
export const BORDER = {
  MAIN: '#E4E6EF',       // Main borders
  LIGHT: '#F5F8FA',      // Light borders
  FOCUS: '#3E97FF',      // Focus state borders
  HOVER: '#A1A5B7',      // Hover state borders
};

// Status Colors
export const STATUS = {
  ACTIVE: {
    MAIN: '#00CC85',
    LIGHT: '#E8FFF3',
    BORDER: '#00CC8530',
  },
  PENDING: {
    MAIN: '#FFA07A',
    LIGHT: '#FFF2CC',
    BORDER: '#FFA07A30',
  },
  INACTIVE: {
    MAIN: '#A1A5B7',
    LIGHT: '#F5F8FA',
    BORDER: '#A1A5B730',
  },
  YES: {
    MAIN: '#34C759',
    LIGHT: '#C6F4D6',
    BORDER: '#34C75930',
  },
  NO: {
    MAIN: '#FF3737',
    LIGHT: '#FFC5C5',
    BORDER: '#FF373730',
  },
  NOT_APPLICABLE: {
    MAIN: '#A1A5B7',
    LIGHT: '#F5F8FA',
    BORDER: '#A1A5B730',
  },
  NEED_ACTION: {
    MAIN: '#FFA07A',
    LIGHT: '#FFF2CC',
    BORDER: '#FFA07A30',
  }
};

// Shadow Colors
export const SHADOW = {
  LIGHT: '0 2px 4px rgba(0,0,0,0.05)',
  MEDIUM: '0 4px 16px rgba(0,0,0,0.1)',
  HEAVY: '0 8px 24px rgba(0,0,0,0.15)',
};

// Common Color Functions
export const getAlphaColor = (hexColor, alpha) => {
  return `${hexColor}${Math.floor(alpha * 255).toString(16).padStart(2, '0')}`;
};

/**
 * Centralized Color Helper
 * 
 * A unified function to get color objects for different types of values (risk levels, risk types, statuses).
 * Each color object contains MAIN, LIGHT, and BORDER colors for consistent styling.
 * 
 * @param {string|number} value - The value to get colors for (e.g., risk level number, risk type string, status string)
 * @param {string} type - The type of color mapping to use ('risk_level', 'risk_type', 'status')
 * @returns {Object} Color object containing { MAIN, LIGHT, BORDER } properties
 * 
 * @example
 * // Get colors for risk levels (1-5)
 * const criticalRiskColors = getColor(5, 'risk_level');
 * // Returns: { MAIN: '#dd435b', LIGHT: '#FFF5F8', BORDER: '#dd435b40' }
 * 
 * @example
 * // Get colors for risk types
 * const infrastructureColors = getColor('Infrastructure', 'risk_type');
 * // Returns: { MAIN: '#1D3DFC', LIGHT: '#F1F3FF', BORDER: '#1D3DFC40' }
 * 
 * @example
 * // Get colors for status
 * const activeStatusColors = getColor('active', 'status');
 * // Returns: { MAIN: '#00CC85', LIGHT: '#E8FFF3', BORDER: '#00CC8530' }
 * 
 * @example
 * // Usage in components with StandardDisplayField
 * <StandardDisplayField
 *   label="Critical"
 *   type="risk"
 *   variant="chip"
 *   customColors={getColor(5, 'risk_level')}
 * />
 * 
 * @example
 * // Usage with specific helper functions
 * <StandardDisplayField
 *   label="Infrastructure"
 *   type="category"
 *   variant="chip"
 *   customColors={getRiskTypeColor('Infrastructure')}
 * />
 */
export const getColor = (value, type = 'risk_level') => {
  switch(type) {
    case 'risk_level':
      switch(value) {
        case 5:
          return RISK_LEVEL.CRITICAL;
        case 4:
          return RISK_LEVEL.HIGH;
        case 3:
          return RISK_LEVEL.MODERATE;
        case 2:
          return RISK_LEVEL.LOW;
        case 1:
          return RISK_LEVEL.VERY_LOW;
        default:
          return {
            MAIN: TEXT.SECONDARY,
            LIGHT: BACKGROUND.LIGHT,
            BORDER: `${BORDER.MAIN}`
          };
      }
    
    case 'risk_type':
      switch(value) {
        case 'Infrastructure':
          return RISK_TYPE.INFRASTRUCTURE;
        case 'Data Breach':
          return RISK_TYPE.DATA_BREACH;
        case 'Supply Chain':
          return RISK_TYPE.SUPPLY_CHAIN;
        case 'Regulatory and Compliance':
          return RISK_TYPE.REGULATORY;
        case 'Human Factor':
          return RISK_TYPE.HUMAN_FACTOR;
        default:
          return {
            MAIN: TEXT.SECONDARY,
            LIGHT: BACKGROUND.LIGHT,
            BORDER: `${BORDER.MAIN}`
          };
      }
    
    case 'risk_category':
      switch(value) {
        case 'Classical/Standard': return RISK_TYPE.INFRASTRUCTURE;
        case 'Cloud':              return RISK_TYPE.SUPPLY_CHAIN;
        case 'OT':                 return RISK_TYPE.REGULATORY;
        case 'AI':                 return RISK_TYPE.DATA_BREACH;
        case 'Others':             return RISK_TYPE.HUMAN_FACTOR;
        default: return { MAIN: TEXT.SECONDARY, LIGHT: BACKGROUND.LIGHT, BORDER: `${BORDER.MAIN}` };
      }
    
    case 'status':
      switch(String(value || '').toLowerCase()) {
        case 'active':
          return STATUS.ACTIVE;
        case 'pending':
          return STATUS.PENDING;
        case 'inactive':
          return STATUS.INACTIVE;
        default:
          return {
            MAIN: TEXT.SECONDARY,
            LIGHT: BACKGROUND.LIGHT,
            BORDER: `${BORDER.MAIN}`
          };
      }
    
    default:
      return {
        MAIN: TEXT.SECONDARY,
        LIGHT: BACKGROUND.LIGHT,
        BORDER: `${BORDER.MAIN}`
      };
  }
};

/**
 * Helper Functions
 * 
 * Convenience wrappers around getColor for specific use cases.
 * These provide backward compatibility and more intuitive naming for specific color types.
 */

/**
 * Get colors for risk levels (1-5)
 * @param {number} level - Risk level (1: Very Low, 2: Low, 3: Moderate, 4: High, 5: Critical)
 * @returns {Object} Color object containing { MAIN, LIGHT, BORDER }
 */
export const getRiskLevelColor = (level) => getColor(level, 'risk_level');

/**
 * Get colors for risk types
 * @param {string} type - Risk type ('Infrastructure', 'Data Breach', 'Supply Chain', 'Regulatory and Compliance', 'Human Factor')
 * @returns {Object} Color object containing { MAIN, LIGHT, BORDER }
 */
export const getRiskTypeColor = (type) => getColor(type, 'risk_type');

/**
 * Get colors for status indicators
 * @param {string} status - Status value ('active', 'pending', 'inactive')
 * @returns {Object} Color object containing { MAIN, LIGHT, BORDER }
 */
export const getStatusColor = (status) => getColor(status, 'status'); 